import { useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addDays, type ProjectionActualReport, type ProjectionScenarioRecord } from '@dairy/shared';
import { projectionLinkEligible } from '@dairy/domain';
import { useSession } from '@/auth/useSession';
import { sessionStore } from '@/auth/session';
import { Button, Card, CardTitle, Dialog, Field, Select, Spinner, Table, Td, Th } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { apiJson } from '@/lib/api';
import { useFormat } from '@/lib/format';
import { useOnline } from '@/features/reports/common';
import { linkProjectionAnimal, ProjectionStorageError } from './projection-storage';
import { ProjectionNotices } from './ProjectionContext';

export function LinkProjectionAnimal({ record }: { record: ProjectionScenarioRecord }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const lock = useRef(false);
  const animals = useLiveQuery(() => db.animals.toArray(), []);
  const eligible = animals?.filter((a) => projectionLinkEligible(record.input.startDate, a));
  const linked = animals?.find((a) => a.id === record.linkedAnimalId && !a.deletedAt);
  const submit = async () => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      await linkProjectionAnimal(record, selected || null);
      setOpen(false);
    } catch (e) {
      setError(e);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      {linked ? (
        <Link className="text-sm text-brand-800 underline" to={`/animals/${linked.id}`}>
          {linked.tag} · {linked.name}
        </Link>
      ) : null}
      <div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setSelected(record.linkedAnimalId ?? '');
            setError(null);
            setOpen(true);
          }}
        >
          {t('projectionActual.link')}
        </Button>
      </div>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!lock.current) setOpen(v);
        }}
        title={t('projectionActual.link')}
        description={t('projectionActual.linkHint')}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field htmlFor={`link-animal-${record.id}`} label={t('projectionActual.animal')}>
            <Select
              id={`link-animal-${record.id}`}
              value={selected}
              disabled={busy || !eligible}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">{t('projectionActual.unlinked')}</option>
              {eligible?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.tag} · {a.name}
                </option>
              ))}
            </Select>
          </Field>
          {error ? (
            <p role="alert" className="text-sm text-red-700">
              {error instanceof ProjectionStorageError
                ? t(`projectionSaved.errors.${error.code}`)
                : errorMessage(t, error)}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" disabled={busy} onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={busy || !eligible || (!!selected && !eligible.some((a) => a.id === selected))}
            >
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

/** Private responses stay in component memory. Owner/farm/record checks also guard in-flight requests. */
export function ProjectionActual({ record }: { record: ProjectionScenarioRecord }) {
  const { t } = useTranslation();
  const f = useFormat();
  const { profile } = useSession();
  const owner = useRef({ tenantId: profile?.tenantId, userId: profile?.userId });
  const hasAccess =
    profile?.role === 'OWNER' &&
    profile.tenantId === owner.current.tenantId &&
    profile.userId === owner.current.userId &&
    (!record.tenantId || record.tenantId === profile.tenantId);
  const online = useOnline();
  const pending = useLiveQuery(() => db.outbox.where('recordId').equals(record.id).count(), [record.id]);
  const [revision, setRevision] = useState(0);
  const key = JSON.stringify([
    profile?.tenantId,
    profile?.userId,
    profile?.role,
    record,
    pending,
    online,
    revision,
  ]);
  const latest = useRef(key);
  latest.current = key;
  const [state, setState] = useState<{ key: string; data?: ProjectionActualReport; error?: unknown } | null>(
    null,
  );
  useEffect(() => {
    if (!online || pending !== 0 || !record.linkedAnimalId || !hasAccess || !profile) return;
    let cancelled = false;
    const active = () => {
      const session = sessionStore.get();
      return (
        !cancelled &&
        latest.current === key &&
        session.status === 'signedIn' &&
        session.profile?.role === 'OWNER' &&
        session.profile.userId === profile.userId &&
        session.profile.tenantId === profile.tenantId
      );
    };
    void apiJson<ProjectionActualReport>(`/reports/projections/${record.id}/actual`, {
      cache: 'no-store',
    }).then(
      (data) => {
        if (active()) setState({ key, data });
      },
      (error: unknown) => {
        if (active()) setState({ key, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [
    key,
    online,
    pending,
    record.id,
    record.linkedAnimalId,
    profile?.role,
    profile?.userId,
    profile?.tenantId,
    hasAccess,
  ]);
  if (!record.linkedAnimalId || !hasAccess) return null;
  const current = state?.key === key ? state : null;
  const ratio = (value: number | null) =>
    value === null ? t('projectionActual.unavailable') : `${value > 0 ? '+' : ''}${f.pct(value)}`;
  return (
    <Card className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle>{t('projectionActual.title')}</CardTitle>
        <Button
          size="sm"
          variant="secondary"
          disabled={!online || pending !== 0}
          onClick={() => setRevision((v) => v + 1)}
        >
          {t('projectionActual.refresh')}
        </Button>
      </div>
      <p className="text-sm text-stone-600">{t('projectionActual.rules')}</p>
      {!online ? (
        <p>{t('reports.needsInternet')}</p>
      ) : pending === undefined ? (
        <Spinner />
      ) : pending !== 0 ? (
        <p>{t('projectionActual.pendingHint')}</p>
      ) : current?.error ? (
        <p role="alert">{errorMessage(t, current.error)}</p>
      ) : !current?.data ? (
        <Spinner />
      ) : (
        <>
          <p className="text-sm">
            {current.data.animal.tag} · {t('projectionActual.asOf', { date: f.date(current.data.asOf) })}
          </p>
          {current.data.months.length === 0 ? (
            <p>{t('projectionActual.noMonths')}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[960px]">
                <thead>
                  <tr>
                    {[
                      'period',
                      'projectedMilk',
                      'actualMilk',
                      'milkDifference',
                      'coverage',
                      'projectedCosts',
                      'actualCosts',
                      'costDifference',
                    ].map((k) => (
                      <Th key={k}>{t(`projectionActual.${k}`)}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {current.data.months.map((m) => (
                    <tr key={m.month}>
                      <Td className="min-w-[230px]">
                        <div>{f.date(m.from)}</div>
                        <div>– {f.date(addDays(m.to, -1))}</div>
                      </Td>
                      <Td className="whitespace-nowrap">{f.liters(m.projectedMl)}</Td>
                      <Td className="whitespace-nowrap">{f.liters(m.actualMl)}</Td>
                      <Td className="whitespace-nowrap">{ratio(m.milkDifference)}</Td>
                      <Td>{t('projectionActual.days', { recorded: m.recordedDays, total: m.days })}</Td>
                      <Td className="whitespace-nowrap">{f.birr(m.projectedCostSantim)}</Td>
                      <Td className="whitespace-nowrap">{f.birr(m.actualCostSantim)}</Td>
                      <Td className="whitespace-nowrap">{ratio(m.costDifference)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          <p className="text-xs text-stone-600">{t('projectionActual.caution')}</p>
        </>
      )}
    </Card>
  );
}

/** Only mounted for owners, so manager profiles never read the private projection tables. */
export function AnimalProjectionCard({ animalId }: { animalId: string }) {
  const records = useLiveQuery(
    () =>
      db.projectionScenarios
        .where('linkedAnimalId')
        .equals(animalId)
        .filter((r) => !r.deletedAt)
        .toArray(),
    [animalId],
  );
  if (!records?.length) return null;
  return (
    <div className="space-y-4">
      <ProjectionNotices />
      {records.map((record) => (
        <div key={record.id} className="space-y-2">
          <Link to={`/projections/${record.id}`} className="font-semibold text-brand-800 underline">
            {record.name}
          </Link>
          <ProjectionActual record={record} />
        </div>
      ))}
    </div>
  );
}
